import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { find, orderBy, toLower } from "lodash";
import axios from "http-axios";

// components (global)
import ButtonProgress from "components/ButtonProgress";
import DialogWrapper from "components/Dialogs/DialogWrapper";
import DialogTitleWrapper from "components/Dialogs/DialogTitleWrapper";

// firebase
import { useFirebase } from "react-redux-firebase";

// form
import { useForm } from "react-hook-form";

// helpers
import { settingsValidate } from "helpers/validation/settingsValidate";

// material-ui
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";

// styles
const useStyles = makeStyles((theme) => ({
  root: {},
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    overflow: "auto",
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}));

const MicroEngineFloorAccessDialog = ({ open, handleClose }) => {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const firebase = useFirebase();
  const { errors, handleSubmit, setValue, register } = useForm();

  // selector
  const uid = useSelector((state) => state.firebase.auth.uid);
  const floors = useSelector((state) =>
    orderBy(
      state.firestore.ordered.floors ?? [],
      ["block_name", "name"],
      ["asc", "asc"]
    )
  );
  const floorAccess = useSelector(
    (state) => state.firestore.ordered.floorAccess ?? []
  );
  const liftAccess = useSelector((state) =>
    orderBy(state.firestore.ordered.liftAccess ?? [], ["name"], ["asc"])
  );
  const selectedPropertyId = useSelector(
    (state) => state.main.selectedProperty
  );
  const selectedVendor = useSelector((state) => state.main.selectedVendor);
  const floorsMemo = useMemo(
    () =>
      floors.map((floor) => {
        // if floor already exists in floorAccess collection and lift access was assigned

        const floorInUse = !!find(
          floorAccess,
          (o) =>
            toLower(o.floor_name) === toLower(floor.name) &&
            (!!o.resident.lift_access || !!o.visitor.lift_access)
        );

        return {
          ...floor,
          disabled: floorInUse,
        };
      }),
    [floors, floorAccess]
  );

  // state
  const [submitting, setSubmitting] = useState(false);

  // effect
  useEffect(() => {
    register("floorId");
    register("residentLiftAccess");
    register("visitorLiftAccess");
  }, [register]);

  // functions
  const onHandleClose = () => {
    if (!submitting) {
      handleClose();
    }
  };

  const onSubmit = (value) => {
    if (!submitting) {
      setSubmitting(true);

      let residentLiftAccess = null;
      let visitorLiftAccess = null;

      if (value.residentLiftAccess) {
        residentLiftAccess = { ...value.residentLiftAccess };

        if (residentLiftAccess.created_at) delete residentLiftAccess.created_at;

        if (residentLiftAccess.created_by)
          delete value.residentLiftAccess.created_by;
      }

      if (value.visitorLiftAccess) {
        // Create a shallow copy of the visitorLiftAccess object
        visitorLiftAccess = { ...value.visitorLiftAccess };

        // Safely delete the properties
        if (visitorLiftAccess.created_at) {
          delete visitorLiftAccess.created_at;
        }

        if (visitorLiftAccess.created_by) {
          delete visitorLiftAccess.created_by;
        }
      }

      firebase
        .auth()
        .currentUser.getIdToken()
        .then((token) => {
          const headers = {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          };

          axios
            .post(
              `jagaCardUpdateFloorAccess`,
              {
                uid,
                property_id: selectedPropertyId,
                floor_id: value.floorId,
                vendor_id: selectedVendor,
                resident_lift_access: residentLiftAccess,
                visitor_lift_access: visitorLiftAccess,
              },
              {
                headers: headers,
              }
            )
            .then((res) => {
              if (res.data.result === "success") {
                setSubmitting(false);
                onHandleClose();
                snackbar.enqueueSnackbar("Floor Access had been added", {
                  variant: "success",
                });
              }
            })
            .catch((e) => {
              setSubmitting(false);
              onHandleClose();
              snackbar.enqueueSnackbar(e.message, {
                variant: "error",
                persist: false,
              });
            });
        });
    }
  };

  return (
    <form
      noValidate
      xs={12}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <DialogWrapper size="md" open={open}>
        <DialogTitleWrapper
          title="Add Door Access"
          handleClose={onHandleClose}
        />

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item container md={12} xs={12} spacing={2}>
              <Grid item md={6} xs={12}>
                <Autocomplete
                  autoHighlight
                  options={floorsMemo}
                  groupBy={(option) => option.block_name}
                  getOptionLabel={(option) => option.name}
                  getOptionDisabled={(option) => option.disabled}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(e, value) => {
                    setValue("floorId", value ? value.id : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      error={!!errors.floor}
                      helperText={
                        errors.floor &&
                        settingsValidate.floor.message[errors.floor.type]
                      }
                      {...params}
                      label="Choose a floor *"
                      fullWidth
                      size="small"
                      name="floor"
                      autoComplete="off"
                      variant="outlined"
                      inputRef={register(settingsValidate.floor.rules)}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item md={12}>
              <Divider />
            </Grid>

            <Grid item container md={6} xs={12} spacing={1}>
              <Grid item md={12} xs={12}>
                <Typography className={classes.modalTitle} variant="h4">
                  Resident
                </Typography>
                <Typography className={classes.modalTitle} variant="h6">
                  Please choose the Lift Access
                </Typography>
              </Grid>
              <Grid item md={12} xs={12}>
                <Autocomplete
                  autoHighlight
                  options={liftAccess}
                  getOptionLabel={(option) =>
                    option.display_name ? option.display_name : option.name
                  }
                  onChange={(e, value) => {
                    setValue("residentLiftAccess", value ? value : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Choose lift access *"
                      fullWidth
                      size="small"
                      name="accessLevel"
                      autoComplete="off"
                      variant="outlined"
                      inputRef={register}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12} spacing={1}>
              <Grid item md={12} xs={12}>
                <Typography className={classes.modalTitle} variant="h4">
                  Visitor
                </Typography>
                <Typography className={classes.modalTitle} variant="h6">
                  Please choose the Lift Access
                </Typography>
              </Grid>
              <Grid item md={12} xs={12}>
                <Autocomplete
                  autoHighlight
                  options={liftAccess}
                  getOptionLabel={(option) =>
                    option.display_name ? option.display_name : option.name
                  }
                  onChange={(e, value) => {
                    setValue("visitorLiftAccess", value ? value : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Choose lift access *"
                      fullWidth
                      size="small"
                      name="accessLevel"
                      autoComplete="off"
                      variant="outlined"
                      inputRef={register}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onHandleClose}>
            Cancel
          </Button>
          <ButtonProgress
            type="submit"
            containName="Save"
            margin={false}
            loading={submitting}
            disabled={submitting}
            onClick={handleSubmit(onSubmit)}
          />
        </DialogActions>
      </DialogWrapper>
    </form>
  );
};

export default MicroEngineFloorAccessDialog;
